import { fabric } from 'fabric';
import type { Editor } from '../editor/editor';
import type { History } from '../editor/history';
import {
  dragBox, NOTE_COLORS, PENS, POLYGON_SIDES, polygonPoints, SHAPES, TAP_MOVE_PX, withAlpha, type PenKind, type ShapeId
} from '../editor/drawing';
import { fontNeeds, loadFonts } from '../fonts/load';
import { $ } from './dom';
import { makeText, SHAPE_FILL, shapeBase } from './objects';
import { toast } from './toast';

/*
 * เครื่องมือ (choices 1ก–6ก): a rail beside the work area, opened from the "เครื่องมือ" tab.
 *   เลือก           the usual: tap to choose, drag to move
 *   ปากกา           ปากกา · มาร์กเกอร์ · ไฮไลต์ — draw with a finger or Apple Pencil (two fingers still zoom/move);
 *                   ยางลบ removes a whole drawn stroke it touches (3ก); colour and thickness in its panel
 *   รูปทรง          9 shapes: drag on the page = that size, tap = the usual size there (4ก)
 *   เส้น            drag = from where to where, tap = the usual line; dashes/round ends/heads on its toolbar (5ก)
 *   โน้ตแปะ         a coloured note on the screen only — never in a file (6ก)
 *   ข้อความ         tap = a text box there, typing at once
 * After a shape, line, note or text is made, the rail goes back to เลือก with it chosen (as in Canva); the pens stay
 * on until another tool is picked. Someone else's design, a video and a page still loading refuse with the reason.
 */

type Mode = 'select' | 'pen' | 'eraser' | 'shape' | 'line' | 'note' | 'text';
type Obj = fabric.Object & { pen?: PenKind; note?: boolean; name?: string };

const svg = (d: string, extra = '') => `<svg viewBox="0 0 24 24"${extra}>${d}</svg>`;
const SHAPE_ICON: Record<ShapeId, string> = {
  square: svg('<rect x="4" y="4" width="16" height="16"/>'),
  rounded: svg('<rect x="4" y="4" width="16" height="16" rx="4"/>'),
  circle: svg('<circle cx="12" cy="12" r="8.5"/>'),
  triangle: svg('<path d="M12 4l9 16H3z"/>'),
  triangleDown: svg('<path d="M12 20L3 4h18z"/>'),
  diamond: svg('<path d="M12 3l9 9-9 9-9-9z"/>'),
  pentagon: svg('<path d="M12 3l9 6.5-3.4 11H6.4L3 9.5z"/>'),
  hexagon: svg('<path d="M7 3.5h10l5 8.5-5 8.5H7L2 12z"/>'),
  octagon: svg('<path d="M8.3 3h7.4L21 8.3v7.4L15.7 21H8.3L3 15.7V8.3z"/>')
};
const PEN_ICON: Record<PenKind | 'eraser', string> = {
  pen: svg('<path d="M4 20l4-1 11-11-3-3L5 16z"/>'),
  marker: svg('<path d="M5 19l3 0 10-10-3-3L5 16zM14 7l3 3"/>', ' style="stroke-width:3"'),
  highlighter: svg('<path d="M6 18l2 2 4-1 8-8-5-5-8 8z"/><path d="M3 21h6"/>'),
  eraser: svg('<path d="M8 20h12M4.5 15.5l9-9 5 5-6 6H9z"/>')
};

const READ_ONLY = 'งานนี้เป็นของคนอื่น เปิดดูได้อย่างเดียว';

export function initDrawTools(ed: Editor, history: History, opts: { canEdit: () => boolean }) {
  const { canvas } = ed;
  const rail = $('drawRail'), fly = $('drawFly'), tab = $('toolsTab');
  let mode: Mode = 'select';
  let pen: PenKind = 'pen';
  const penColor: Record<PenKind, string> = { pen: PENS.pen.color, marker: PENS.marker.color, highlighter: PENS.highlighter.color };
  const penWidth: Record<PenKind, number> = { pen: PENS.pen.width, marker: PENS.marker.width, highlighter: PENS.highlighter.width };
  let shape: ShapeId = 'square';
  let noteColor = NOTE_COLORS[0];
  let flyFor: 'pen' | 'shape' | 'note' | null = null;

  /** why a tool that adds to the page cannot be used now, or null */
  function cannot(): string | null {
    if (!opts.canEdit()) return READ_ONLY;
    if (ed.doc.video) return 'บนวิดีโอใช้เครื่องมือวาดไม่ได้ ใช้ "ข้อความ/รูป" ของวิดีโอแทน';
    if (history.loading) return 'กำลังเปิดหน้าอยู่ รอสักครู่แล้วลองใหม่';
    return null;
  }

  /* ---------- modes ---------- */

  function setMode(m: Mode) {
    if (m !== 'select') { const why = cannot(); if (why) { toast(why, true); m = 'select'; } }
    cancelDrag();
    mode = m;
    canvas.isDrawingMode = m === 'pen';
    const placing = m !== 'select' && m !== 'pen';
    canvas.selection = m === 'select' && opts.canEdit();   // someone else's work stays without a selection box
    canvas.skipTargetFind = placing;
    canvas.defaultCursor = placing ? 'crosshair' : 'default';
    (canvas as unknown as { targetFindTolerance: number }).targetFindTolerance = m === 'eraser' ? 10 : 0;
    if (m !== 'select') { canvas.discardActiveObject(); canvas.requestRenderAll(); }
    if (m === 'pen') applyBrush();
    paintRail();
  }

  function applyBrush() {
    const b = new fabric.PencilBrush(canvas);
    b.color = withAlpha(penColor[pen], PENS[pen].alpha);
    b.width = penWidth[pen] * ed.unit();
    (b as unknown as { decimate: number }).decimate = 2;
    b.strokeLineCap = 'round'; b.strokeLineJoin = 'round';
    canvas.freeDrawingBrush = b;
  }

  // a finished pen stroke: mark it as one (the eraser removes only these), named for the layer list
  canvas.on('path:created', (e: unknown) => {
    const p = (e as { path?: Obj }).path;
    if (!p) return;
    // a page loading (BUG-10: it would land on the incoming page, unrecorded), someone else's work, a video
    const why = cannot();
    if (why) { history.silently(() => canvas.remove(p)); toast(why, true); return; }
    p.set({ pen, name: PENS[pen].label } as Partial<Obj>);
  });

  /* ---------- the rail and its panel ---------- */

  function paintRail() {
    rail.querySelectorAll<HTMLButtonElement>('[data-m]').forEach(b => {
      const m = b.dataset.m!;
      b.setAttribute('aria-pressed', String(m === mode || (m === 'pen' && mode === 'eraser')));
    });
    paintFly();
  }

  function paintFly() {
    fly.hidden = !flyFor;
    fly.textContent = '';
    if (flyFor === 'pen') {
      for (const k of ['pen', 'marker', 'highlighter', 'eraser'] as const) {
        const b = button(k === 'eraser' ? 'ยางลบ (แตะเส้นที่วาดเพื่อลบ)' : PENS[k].label, PEN_ICON[k], () => {
          if (k === 'eraser') setMode('eraser'); else { pen = k; setMode('pen'); }
        });
        b.setAttribute('aria-pressed', String(k === 'eraser' ? mode === 'eraser' : mode === 'pen' && pen === k));
        fly.append(b);
      }
      if (mode !== 'eraser') {
        const color = Object.assign(document.createElement('input'), { type: 'color', value: penColor[pen], title: 'สีปากกา' });
        color.setAttribute('aria-label', 'สีปากกา');
        color.oninput = () => { penColor[pen] = color.value; applyBrush(); };
        const width = Object.assign(document.createElement('input'), { type: 'range', min: '1', max: '80', value: String(penWidth[pen]) });
        width.setAttribute('aria-label', 'ความหนาปากกา');
        width.className = 'drawwidth';
        width.oninput = () => { penWidth[pen] = Number(width.value); applyBrush(); };
        fly.append(color, width);
      }
    } else if (flyFor === 'shape') {
      for (const s of SHAPES) {
        const b = button(s.label, SHAPE_ICON[s.id], () => { shape = s.id; setMode('shape'); hint('ลากบนหน้าเพื่อวาดตามขนาดที่ต้องการ หรือแตะบนหน้าเพื่อวางขนาดปกติ'); });
        b.setAttribute('aria-pressed', String(mode === 'shape' && shape === s.id));
        fly.append(b);
      }
    } else if (flyFor === 'note') {
      for (const c of NOTE_COLORS) {
        const b = button('โน้ตสีนี้', `<span class="drawswatch" style="background:${c}"></span>`, () => { noteColor = c; setMode('note'); hint('แตะบนหน้าเพื่อแปะโน้ต (โน้ตแสดงบนจอเท่านั้น ไม่อยู่ในไฟล์ที่ส่งออก)'); });
        b.setAttribute('aria-pressed', String(mode === 'note' && noteColor === c));
        fly.append(b);
      }
    }
  }

  function button(label: string, icon: string, run: () => void) {
    const b = document.createElement('button');
    b.innerHTML = icon; b.setAttribute('aria-label', label); b.title = label;
    b.onclick = run;
    return b;
  }
  /** one hint per tool choice, not on every tap */
  let lastHint = '';
  const hint = (t: string) => { if (t !== lastHint) { lastHint = t; toast(t); } };

  rail.querySelectorAll<HTMLButtonElement>('[data-m]').forEach(b => {
    b.onclick = () => {
      const m = b.dataset.m as Mode;
      if (m === 'pen' || m === 'shape' || m === 'note') {
        flyFor = flyFor === m && (mode === m || (m === 'pen' && mode === 'eraser')) ? null : m;
        if (m === 'pen') setMode(mode === 'eraser' ? 'eraser' : 'pen');
        else if (m === 'shape') { setMode('shape'); hint('ลากบนหน้าเพื่อวาดตามขนาดที่ต้องการ หรือแตะบนหน้าเพื่อวางขนาดปกติ'); }
        else { setMode('note'); hint('แตะบนหน้าเพื่อแปะโน้ต (โน้ตแสดงบนจอเท่านั้น ไม่อยู่ในไฟล์ที่ส่งออก)'); }
        if (mode === 'select') flyFor = null;   // refused (read-only, video, loading)
        paintRail();
        return;
      }
      flyFor = null;
      setMode(m);
      if (m === 'line') hint('ลากบนหน้าจากต้นเส้นไปปลายเส้น หรือแตะเพื่อวางเส้นปกติ');
      if (m === 'text') hint('แตะบนหน้าตรงที่จะพิมพ์');
    };
  });
  $('drawClose').onclick = () => close();

  function open() {
    rail.hidden = false; tab.setAttribute('aria-pressed', 'true');
    setMode('select');
  }
  function close() {
    flyFor = null;
    setMode('select');
    rail.hidden = true; fly.hidden = true; tab.setAttribute('aria-pressed', 'false');
  }
  tab.onclick = () => { if (rail.hidden) open(); else close(); };
  document.addEventListener('keydown', e => {
    if (e.key === 'Escape' && mode !== 'select' && !(canvas.getActiveObject() as fabric.IText | null)?.isEditing) setMode('select');
  });

  /* ---------- placing: shapes, lines, notes, text (drag = size, tap = the usual size) ---------- */

  let drag: { start: fabric.Point; screen: { x: number; y: number }; made: fabric.Object | null } | null = null;

  canvas.on('mouse:down', (opt: fabric.IEvent) => {
    if (!opt.e || (mode !== 'shape' && mode !== 'line' && mode !== 'note' && mode !== 'text' && mode !== 'eraser')) return;
    const why = cannot();
    if (why) { toast(why, true); setMode('select'); return; }
    if (mode === 'eraser') {
      erased = 0; missed = null;
      erasePending = { at: canvas.getPointer(opt.e, true), t: opt.e.timeStamp, e: opt.e };
      return;
    }
    drag = { start: canvas.getPointer(opt.e) as fabric.Point, screen: canvas.getPointer(opt.e, true), made: null };
  });
  canvas.on('mouse:move', (opt: fabric.IEvent) => {
    if (mode === 'eraser' && opt.e && (erasing || erasePending)) {
      if (!erasing) {
        const s = canvas.getPointer(opt.e, true), pend = erasePending!;
        // a few px or a moment first: the first finger of a pinch lands ~30 ms before the second
        if (Math.hypot(s.x - pend.at.x, s.y - pend.at.y) < 4 && opt.e.timeStamp - pend.t < 60) return;
        erasePending = null; erase(pend.e);
      }
      erase(opt.e); return;
    }
    if (!drag || !opt.e) return;
    const s = canvas.getPointer(opt.e, true);
    if (!drag.made && Math.hypot(s.x - drag.screen.x, s.y - drag.screen.y) < TAP_MOVE_PX) return;
    const p = canvas.getPointer(opt.e) as fabric.Point;
    if (!drag.made) {
      drag.made = make(mode, null);
      if (!drag.made) { drag = null; return; }
      // not an undo step yet: the shape is still being dragged out (its final size is one step, in finish — BUG-51)
      const made = drag.made;
      history.silently(() => canvas.add(made));
    }
    sizeTo(drag.made, drag.start, p, !!(opt.e as MouseEvent).shiftKey);
    canvas.requestRenderAll();
  });
  canvas.on('mouse:up', (opt: fabric.IEvent) => {
    if (mode === 'eraser') { if (erasePending) { const pend = erasePending; erasePending = null; erase(pend.e); } endErase(); return; }
    const d = drag; drag = null;
    if (!d || !opt.e) return;
    void finish(d.made, d.start);
  });

  /** a second finger: the press was the start of a pinch, not a shape (nothing is left half-made) */
  document.addEventListener('touchstart', e => { if (e.touches.length > 1) cancelDrag(); }, { capture: true, passive: true });
  function cancelDrag() {
    const made = drag?.made;
    if (made) history.silently(() => canvas.remove(made));
    drag = null;
    if (erasing || erasePending) { erasing = false; erasePending = null; if (erased) history.push(); erased = 0; missed = null; }
  }

  /** a new object for the mode, the usual size at `at` (or at 0,0 to be sized by a drag) */
  function make(m: Mode, at: fabric.Point | null): fabric.Object | null {
    const u = ed.unit();
    if (m === 'line') {
      const len = 400 * u, x = at ? at.x - len / 2 : 0, y = at ? at.y : 0;
      return new fabric.Line([x, y, x + len, y], { stroke: '#1D1A2B', strokeWidth: 8 * u, strokeUniform: true, strokeLineCap: 'round' });
    }
    if (m === 'note') {
      const t = makeText('โน้ต', { fontSize: 36 * u, width: 340 * u, fill: '#2b2400', backgroundColor: noteColor, lineHeight: 1.3 });
      (t as unknown as Obj).set({ note: true, name: 'โน้ตแปะ' } as Partial<Obj>);
      if (at) t.set({ left: at.x - (t.width ?? 0) / 2, top: at.y - (t.height ?? 0) / 2 });
      return t;
    }
    if (m === 'text') {
      const t = makeText('พิมพ์ข้อความ', { fontSize: 56 * u, width: 620 * u });
      if (at) t.set({ left: at.x, top: at.y - (t.height ?? 0) / 2 });
      return t;
    }
    if (m !== 'shape') return null;
    const S = 360 * u, base = { ...shapeBase, fill: SHAPE_FILL, left: 0, top: 0 };
    let o: fabric.Object;
    const sides = POLYGON_SIDES[shape];
    if (shape === 'square' || shape === 'rounded') o = new fabric.Rect({ ...base, width: S, height: S, rx: shape === 'rounded' ? S * 0.12 : 0, ry: shape === 'rounded' ? S * 0.12 : 0 });
    else if (shape === 'circle') o = new fabric.Circle({ ...base, radius: S / 2 });
    else if (shape === 'triangle' || shape === 'triangleDown') o = new fabric.Triangle({ ...base, width: S, height: S * 0.87, flipY: shape === 'triangleDown' });
    else o = new fabric.Polygon(polygonPoints(sides!, S, S), { ...base });
    (o as Obj).name = SHAPES.find(s => s.id === shape)!.label;
    if (at) o.set({ left: at.x - o.getScaledWidth() / 2, top: at.y - o.getScaledHeight() / 2 });
    return o;
  }

  /** stretch an object being dragged out between `a` and `b` */
  function sizeTo(o: fabric.Object, a: fabric.Point, b: fabric.Point, square: boolean) {
    if (o.type === 'line') { (o as fabric.Line).set({ x1: a.x, y1: a.y, x2: b.x, y2: b.y }); o.setCoords(); return; }
    const box = dragBox(a, b, square);
    const w = Math.max(4, box.w), h = Math.max(4, box.h);
    if (o.type === 'rect') {
      const r = o as fabric.Rect, round = (r.rx ?? 0) > 0;
      r.set({ width: w, height: h, rx: round ? Math.min(w, h) * 0.12 : 0, ry: round ? Math.min(w, h) * 0.12 : 0 });
    } else if (o.type === 'thaiTextbox') {
      o.set({ width: w });
      (o as fabric.Textbox).initDimensions();
    } else {
      o.set({ scaleX: w / (o.width ?? 1), scaleY: h / (o.height ?? 1) });
    }
    o.set({ left: box.x, top: box.y });
    o.setCoords();
  }

  /** the tap or drag is over: a tap places the usual size there; then back to เลือก with it chosen */
  async function finish(dragged: fabric.Object | null, at: fabric.Point) {
    const m = mode, page = history.currentPage();
    const drop = () => { if (dragged) history.silently(() => canvas.remove(dragged)); };
    let why = cannot();
    if (why) { drop(); toast(why, true); setMode('select'); return; }
    if (m === 'text' || m === 'note') {
      // the keyboard opens only inside the tap itself (iPad): wait for the font only when it is not there yet
      if (!fontLoaded() && !await fontsReady()) { drop(); return; }
      // the page may have changed or started loading while the font came (it would land on another page)
      why = history.currentPage() !== page ? 'เปลี่ยนหน้าระหว่างรอฟอนต์ จึงยังไม่ได้เพิ่ม แตะใหม่อีกครั้ง' : cannot();
      if (why) { drop(); toast(why, true); setMode('select'); return; }
      if (dragged) (dragged as fabric.Textbox).initDimensions();   // measured again with the real font
    }
    let o = dragged;
    if (!o) { o = make(m, at); if (!o) return; history.silently(() => canvas.add(o!)); }
    o.setCoords();
    setMode('select');
    canvas.setActiveObject(o); ed.paint(); canvas.requestRenderAll();
    canvas.fire('object:modified', { target: o });       // its final size is one undo step
    if (o.type === 'thaiTextbox') {
      const t = o as fabric.IText;
      t.enterEditing(); t.selectAll();
    }
  }
  const fontLoaded = () => { try { return document.fonts.check('400 16px "Sarabun"'); } catch { return false; } };
  async function fontsReady(): Promise<boolean> {
    const failed = await loadFonts(fontNeeds([{ type: 'thaiTextbox', fontFamily: 'Sarabun', fontWeight: 400 }]));
    if (failed.length) { toast('โหลดฟอนต์ Sarabun ไม่สำเร็จ ตรวจอินเทอร์เน็ตแล้วลองใหม่ ยังไม่ได้เพิ่ม', true); return false; }
    return true;
  }

  /* ---------- ยางลบ: a whole drawn stroke it touches (3ก) ---------- */

  let erasing = false, erased = 0;
  let erasePending: { at: { x: number; y: number }; t: number; e: Event } | null = null;
  /** something that is not a drawn stroke was under the eraser (said once at the end, if nothing was erased) */
  let missed: string | null = null;
  function erase(e: Event) {
    erasing = true;
    const v = canvas.getPointer(e, true), at = new fabric.Point(v.x, v.y);
    const objs = ed.userObjects() as Obj[];
    for (let k = objs.length - 1; k >= 0; k--) {
      const o = objs[k];
      if (o.visible === false) continue;
      o.setCoords();
      if (!o.containsPoint(at) || canvas.isTargetTransparent(o, v.x, v.y)) continue;
      // one undo step for the whole rub (pushed in endErase), not one per 250 ms
      if (o.type === 'path' && o.pen) { history.silently(() => canvas.remove(o)); erased++; canvas.requestRenderAll(); }
      else if (!missed) missed = (o.name || o.type) ?? 'ชิ้นงาน';
      return;   // the top-most thing under the finger only
    }
  }
  function endErase() {
    if (!erasing) return;
    erasing = false;
    if (erased) { history.push(); toast(`ลบเส้นที่วาด ${erased} เส้น (เลิกทำได้)`); }
    else if (missed) toast(`ยางลบลบได้เฉพาะเส้นที่วาดด้วยปากกา "${missed}" ไม่ใช่เส้นวาด ใช้ปุ่มลบของชิ้นงานแทน`, true);
    erased = 0; missed = null;
  }

  return { close, get mode() { return mode; } };
}
